import type {SerializedStyles} from '@emotion/react';
import type {NavLinkProps} from 'react-router-dom';

import {css} from '@emotion/react';
import React from 'react';
import {matchPath, NavLink, useLocation, useMatch} from 'react-router-dom';

import {useColor} from '../styles';

const isMatch = (paths?: string[], match?: string) => {
  if (!paths || !match) return false;
  return !!paths.find((path) => matchPath(path, match));
};

interface ExtendedNavLinkProps extends NavLinkProps {
  css?: SerializedStyles;
  matches?: string[];
}

/**
 * ExtendedNavLink is a styled NavLink that supports custom active matching and styles.
 *
 * @component
 * @param {ExtendedNavLinkProps} props - Props for the ExtendedNavLink component.
 * @returns {JSX.Element} The rendered navigation link element.
 */
const ExtendedNavLink = (props: ExtendedNavLinkProps): JSX.Element => {
  const {matches, to, css: cssProp, ...navLinkProps} = props;
  const {palette} = useColor();
  const location = useLocation();

  const pathName = typeof to === 'string' ? to : to.pathname || '';
  const isActive = !!useMatch(pathName) || isMatch(matches, location.pathname);

  const activeStyle =
    isActive &&
    css({
      '&': {
        color: palette.content.accent.defaultColor,
        '&:hover': {
          color: `${palette.content.accent.defaultColor} !important`,
        },
      },
    });

  const mergedContainerCss = css([cssProp, activeStyle]);

  return (
    <NavLink
      css={mergedContainerCss}
      to={to}
      {...navLinkProps}
    />
  );
};

export default ExtendedNavLink;
